import {
  ISanityBlockContent,
  ISanityImage,
  ISanityLocaleBlockContent,
  ISanityLocaleImages,
} from '@rbi-ctg/menu';
import { isTest } from 'utils/environment';
import * as Environment from 'utils/environment';
import { replaceKeyRegex } from 'utils/language';

interface ISanityContentField {
  localeRaw?: ISanityBlockContent[];
  locale?: { app?: ISanityImage; imageDescription?: string };
}

export const getLocaleTextBlock = (content: Record<string, ISanityContentField>, type: string) =>
  content[type].localeRaw;

export const getLocaleImage = (content: Record<string, ISanityContentField>, type: string) => ({
  image: content[type].locale?.app,
  alt: content[type].locale?.imageDescription,
});

export const parseSanityBlock = (
  sanityBlock: ISanityBlockContent[],
  key: string,
  value: string
) => {
  if (sanityBlock && sanityBlock.length) {
    return sanityBlock.map((b: ISanityBlockContent) => ({
      ...b,
      children: b.children.map(c => ({
        ...c,
        text: c.text.replace(replaceKeyRegex(key), value),
      })),
    }));
  }
  return sanityBlock;
};

export const parseSanityBlockMultipleValues = (
  sanityBlock: ISanityBlockContent[],
  values: { [key: string]: string }
): ISanityBlockContent[] =>
  Object.entries(values).reduce(
    (acc, [key, value]) => parseSanityBlock(acc, key, value),
    sanityBlock
  );

export function parseSanityObject<T>({
  doc,
  localeBlockKeys,
  variables,
}: {
  doc: T;
  localeBlockKeys: string[];
  variables: { [key: string]: string };
}) {
  const augmentedDoc: Record<string, unknown> = {};
  localeBlockKeys.forEach(key => {
    try {
      // Allow nested keys, separated by decimal (.) in the form 'a.b'
      if (key.includes('.')) {
        const parts = key.split('.');
        const num = parts.length;
        // only support one layer of nesting
        if (num !== 2) {
          if (!isTest) {
            // eslint-disable-next-line no-console
            console.warn(
              'utils/sanity: parseSanityObject only supports one level of nested `localeBlockKeys`, e.g. "a.b" '
            );
          }

          return;
        }
        const text = getLocaleTextBlock(doc as Record<string, ISanityContentField>, key);
        const parentKey = parts[0];
        const childKey = parts[1];
        if (text && parentKey && childKey) {
          const res = parseSanityBlockMultipleValues(text, variables);
          // If we have already copied over and replaced the `key`'s object. Use that value
          // Otherwise copy the values over.
          const rest = (augmentedDoc[parentKey] || (doc as Record<string, unknown>)[parentKey]) as
            | Record<string, unknown>
            | undefined;
          augmentedDoc[parentKey] = {
            ...rest,
            [childKey]: {
              localeRaw: res,
            },
          };
        }
        return;
      }

      const localeTextBlock = getLocaleTextBlock(doc as Record<string, ISanityContentField>, key);
      if (localeTextBlock) {
        augmentedDoc[key] = {
          localeRaw: parseSanityBlockMultipleValues(localeTextBlock, variables),
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`parseSanityObject - localeBlockKey [${key}] not found`, error);
    }
  });
  return {
    ...doc,
    ...augmentedDoc,
  };
}

export const getFirstStringInLocaleBlockContent = (
  localeBlockContent: ISanityLocaleBlockContent | undefined | null
): string => localeBlockContent?.localeRaw?.[0]?.children?.[0]?.text || '';

export const getDescriptionFromSanityLocaleImages = (
  sanityLocaleImages: ISanityLocaleImages
): string => sanityLocaleImages?.locale?.imageDescription || '';

// TODO: This method is not handling line breaks
export const blockContentToPlainText = (contentBlocks: ISanityBlockContent[] = []): string =>
  contentBlocks
    .map(block => {
      // if it's not a text block with children,
      // return an empty string
      if (block._type !== 'block' || !block.children) {
        return '';
      }
      // loop through the children spans, and join the
      // text strings
      return block.children.map(({ text }) => text).join('');
    })
    .join(' ')
    .trim();

export const emptySanityImage = (): ISanityImage => ({
  asset: {
    _id: '',
    url: '',
  },
});

const env = String(Environment.env()).trim().toLowerCase();
const region = String(Environment.region).trim().toLowerCase();

const SANITY_URL = `https://${env}-bk-${region}-whitelabel-cms.rbi.tools/desk/orderLevelContent;menu;`;

export const getSanityItemUrl = (itemId: string) => `${SANITY_URL}item;${itemId}`;
